import {
  ChannelMessageDoc,
  IssueCaseMessageLinkDoc,
  noTriggerChannelMessagesPath,
  triggerChannelMessagesPath,
} from '@gooduncles/gu-app-schema';
import useSWR from 'swr';

import { FirebaseManager } from 'src/utils/firebase/firebase-manager';

const firebaseManager = FirebaseManager.getInstance();

/**
 * 링크가 가리키는 ChannelMessage 본문을 일괄 조회. 링크 집합 자체가 캐시 단위 —
 * 같은 (collection, messageId) 조합이면 다른 IssueCase 에서도 hit. miss 시 doc 자리는 null.
 *
 * 캐시 키는 (messageCollection/messageId) 쌍의 정렬 합. 링크가 한 건이라도 추가/삭제되면
 * 키가 바뀌어 전체 재조회되지만, 보통 N <= 10 이라 비용은 무시할 수준.
 */
const fetchLinkedMessageDocs = async (
  links: IssueCaseMessageLinkDoc[],
): Promise<Record<string, ChannelMessageDoc | null>> => {
  const entries = await Promise.all(
    links.map(async (link) => {
      const path =
        link.messageCollection === 'triggerChannelMessage' ? triggerChannelMessagesPath : noTriggerChannelMessagesPath;
      const doc = await firebaseManager.getDoc<ChannelMessageDoc>(`${path}/${link.messageId}`);
      if (!doc) {
        console.error('[useLinkedMessageDocs] 메시지 문서를 찾을 수 없습니다.', {
          linkId: link._id,
          messageId: link.messageId,
          messageCollection: link.messageCollection,
        });
      }
      return [link.messageId, doc ?? null] as const;
    }),
  );
  return Object.fromEntries(entries);
};

const useLinkedMessageDocs = (links: IssueCaseMessageLinkDoc[]) => {
  const messageRefsKey = links
    .map((link) => `${link.messageCollection}/${link.messageId}`)
    .sort()
    .join('|');
  const key = links.length > 0 ? [messageRefsKey, 'linked-message-docs'] : null;

  const { data, error, isLoading } = useSWR(key, () => fetchLinkedMessageDocs(links), {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    errorRetryCount: 2,
  });

  return {
    messageDocs: data ?? {},
    isLoading,
    error,
  };
};

export default useLinkedMessageDocs;
